// THE `+`, and everything about it that is not a decision about what riksu creates.
//
// The list lives in `app/creations.ts`; this renders whatever it is handed. The split is
// the same one the rail has with `destinations.ts`, and it is what makes adding a
// creation one declaration line: nothing in this file names leave, expense or anything
// else, so nothing here has to change when the product grows a create flow.
//
// # Nothing is hand-rolled, and that is the point of the file being this short
//
// The menu is Base UI's, through the generated `dropdown-menu`. A hand-written one loses
// the focus trap, `Escape`, the focus restore, `aria-haspopup`/`aria-expanded`, the
// arrow-key roving and the typeahead — silently, all at once, and each of them is a bug
// somebody finds later with a keyboard rather than a test.
//
// The trigger is `IconAction`, where the label is a REQUIRED prop feeding both the
// `aria-label` and the tooltip from one string. A `+` is a glyph and a glyph is not a
// name: without one, a screen reader announces "button" and a sighted person meeting the
// control learns what it does by pressing it.
//
// # An empty menu renders NO BUTTON
//
// Not a disabled one, and not a `+` that opens onto "nothing here". A caller who can
// create nothing is not being told "not yet" the way the rail's settings and help are —
// those exist and are unbuilt, whereas this is a person whose token reaches none of the
// four flows, and a control that opens onto an empty list is a control that lies about
// what pressing it will do.
//
// # The motion is riksu's, over the generated component's dead classes
//
// `components/ui/dropdown-menu.tsx` ships `animate-in`, `zoom-in-95` and four
// `slide-in-from-*` from `tw-animate-css`, which this repository deliberately does not
// install — so all of them paint nothing, and what is left is a bare `duration-100` on
// `transition-property: all`. `menuMotion` is the written, tokenised answer, and the
// depth and shadow come from the named steps rather than from `z-50` and `shadow-md`.
// Overridden rather than edited, because `shadcn add` puts them straight back.

import type { LucideIcon } from "lucide-react";
import { PlusIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { IconAction } from "./button";
import { menuMotion } from "./motion";

/** One thing that can be created, already translated. */
export interface CreateChoice {
  /** Stable identity, handed back to the caller. Never rendered. */
  readonly id: string;
  readonly label: string;
  readonly icon: LucideIcon;
}

export interface CreateMenuProps {
  /** The trigger's accessible name AND its tooltip, from one string. */
  readonly label: string;
  readonly choices: readonly CreateChoice[];
  readonly onChoose: (id: string) => void;
  /** The shell's own colouring for a control sitting in the dark topbar. */
  readonly className?: string;
}

export function CreateMenu({ label, choices, onChoose, className }: CreateMenuProps) {
  // NO BUTTON RATHER THAN AN EMPTY ONE. See the note above: this is not the rail's
  // "disabled says not yet" case, it is a caller for whom none of these flows exists.
  if (choices.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      {/* The children are the trigger's; `render` supplies the element they land in.
          `IconAction` spreads everything Base UI merges in — the ref, the click handler,
          `aria-haspopup` and `aria-expanded` — onto the real `<button>` underneath, so
          the menu's wiring reaches the control rather than stopping at the tooltip. */}
      <DropdownMenuTrigger render={<IconAction label={label} className={className} />}>
        <PlusIcon className="size-4" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        // ANCHORED TO ITS RIGHT EDGE, because the trigger lives at the right end of the
        // topbar and a menu aligned to the left of it would hang off the window at 420px.
        align="end"
        className={`animate-none ${menuMotion} z-(--layer-popover) w-60 shadow-(--shadow-overlay)`}
      >
        {choices.map((choice) => (
          <DropdownMenuItem
            key={choice.id}
            className="gap-2 px-2 py-1.5 text-shell-body"
            onClick={() => onChoose(choice.id)}
          >
            <choice.icon className="size-4" aria-hidden="true" />
            {choice.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
